import socketInstance from '@/connections/socket.instance';
import { refreshSession } from '@/connections/axios.instance';
import registerSocketHandlers from '@/lib/socket/socket.handlers';
import socketListenerTracker from '@/lib/socket/socket.listener.tracker';
import applyHandshakeToken from '@/lib/socket/socket.handshake.token';
import socketConnectError from '@/lib/socket/socket.connect.error';
import socketReconnectDelay from '@/lib/socket/socket.reconnect.delay';
import { SOCKET_SESSION_EVENTS } from '@/lib/socket/socket.session.events';
import { tokenManager } from '@/lib/session/token.manager';
import type { TypeAppDispatch } from '@/store/store';

let started = false;
let release: (() => void) | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retrying = false;
let attempts = 0;
let replaced = false;

const connectWithToken = (socket: ReturnType<typeof socketInstance>) => {
    applyHandshakeToken(socket);
    socket.connect();
};

const cancelRetry = () => {
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = null;
};

const scheduleRetry = (refreshFirst: boolean) => {
    if (!started || replaced || retryTimer || retrying) return;
    const delay = socketReconnectDelay(attempts);
    attempts += 1;
    retryTimer = setTimeout(async () => {
        retryTimer = null;
        retrying = true;
        try {
            if (refreshFirst && !(await refreshSession())) return;
            if (!started || !tokenManager.get()) return;
            const socket = socketInstance();
            if (!socket.connected && !socket.active) connectWithToken(socket);
        } finally {
            retrying = false;
        }
    }, delay);
};

const onToken = (token: string | null) => {
    const socket = socketInstance();

    if (!token) {
        cancelRetry();
        socket.disconnect();
        return;
    }

    if (replaced || socket.connected) return;
    if (!socket.active && !retryTimer && !retrying) connectWithToken(socket);
};

const onConnect = () => {
    attempts = 0;
    cancelRetry();
};

const onConnectError = (error: unknown) => {
    const socket = socketInstance();
    if (socket.active) return;
    const kind = socketConnectError(error);
    if (kind === 'fatal') return;
    scheduleRetry(kind === 'auth');
};

const onDisconnect = (reason: string) => {
    if (reason === 'io server disconnect') scheduleRetry(true);
};

const onReplaced = () => {
    replaced = true;
    cancelRetry();
};

export const realtimeReconnect = (): void => {
    if (!started || replaced || !tokenManager.get()) return;
    cancelRetry();
    attempts = 0;
    const socket = socketInstance();
    socket.disconnect();
    connectWithToken(socket);
};

export const startRealtime = (dispatch: TypeAppDispatch): void => {
    if (started) return;
    started = true;
    replaced = false;
    attempts = 0;

    const socket = socketInstance();
    const tracker = socketListenerTracker(socket);
    registerSocketHandlers(tracker.socket, dispatch);

    tracker.socket.on('connect', onConnect);
    tracker.socket.on('connect_error', onConnectError);
    tracker.socket.on('disconnect', onDisconnect);
    tracker.socket.on(SOCKET_SESSION_EVENTS.REPLACED, onReplaced);

    const unsubscribe = tokenManager.subscribe(onToken);

    release = () => {
        unsubscribe();
        tracker.release();
    };

    if (tokenManager.get()) connectWithToken(socket);
};

export const stopRealtime = (): void => {
    if (!started) return;
    started = false;
    cancelRetry();
    release?.();
    release = null;
    socketInstance().disconnect();
};
